import { action, makeObservable, observable, runInAction, computed } from 'mobx';
import type { ISavedView } from '@featit/shared';
import {
  type SavedViewDataSourceRegistry,
  savedViewDataSourceRegistry,
} from '@/data-sources/SavedViewDataSourceRegistry';
import { failureToSay, type HeldSentenceType, pageWords, sayEach } from '@/services/actionFailure';
import { createInFlightShare } from '@/services/inFlightShare';
import {
  builtInRows,
  builtInSeedIdOf,
  hierarchyRows,
  treeIdOf,
} from '@/features/views/viewHierarchyRows';
import type { MessageKey } from '@featit/shared/i18n/messages/index';

/**
 * A work area's custom views and folders, shaped like `DashboardPageStore.ts` (F-0201) and holding
 * its two writes (F-0363 slice 2, D-0241: a) — reordering one level and moving a row to another
 * folder — each optimistic, with a rollback that puts back exactly what was on screen and shows
 * the server's own refusal sentence where it has one.
 *
 * Deliberately smaller than `DashboardPageStore.ts`: it does not carry that store's local-first
 * cache and periodic re-verification (`DatasetSyncController`, `localSyncStore`) — infrastructure
 * built for the dashboard's own desktop-binding story, not asked for by F-0363's acceptance
 * criteria. Flagged for the team lead rather than ported silently. What *is* ported, verbatim in
 * shape, is the optimistic-update-then-rollback contract `movePage`/`reorderPages` hold.
 *
 * The one holder of a work area's saved views (I-1064, AC-19) — `ViewStore.savedViews`,
 * `CustomViewsPage.tsx`'s own `views`, `DashboardPage.tsx`'s saved-view block picker and
 * `WorkItemSavedViewSelector.tsx`'s dropdown all read `views(projectId)` here rather than keeping
 * a copy of their own. `ensureProject` is the one load path: loaded or loading for that work area
 * already → no request. `ViewStore`'s own write actions (`saveCurrentView`/`deleteView`) and its
 * composing state (the current layout/filter/sort a reader is composing) are a different concern
 * and still write through `savedViewDataSourceRegistry` directly, nudging this store's `refresh`
 * afterward — this store's own optimistic order must never race a write that knows nothing about
 * it, which is why a write is never folded into this store's own copy optimistically either.
 */
export class ViewHierarchyStore {
  viewsByProjectId: Record<string, ISavedView[]> = {};
  loadingProjectIds: string[] = [];
  /** T-0471: what `errorsByProjectId` says, kept, so it is written in the page's language when read. */
  heldErrorsByProjectId: Record<string, HeldSentenceType | undefined> = {};
  get errorsByProjectId(): Record<string, string | undefined> {
    const held = this.heldErrorsByProjectId;
    return sayEach(pageWords(), held);
  }
  reorderingProjectIds: string[] = [];
  /** T-0471: what `orderErrorsByProjectId` says, kept, so it is written in the page's language when read. */
  heldOrderErrorsByProjectId: Record<string, HeldSentenceType | undefined> = {};
  get orderErrorsByProjectId(): Record<string, string | undefined> {
    const held = this.heldOrderErrorsByProjectId;
    return sayEach(pageWords(), held);
  }

  private readonly requestSequences = new Map<string, number>();
  /**
   * One request per work area while `refresh` is already in flight (I-1064): `ViewNavigationList`'s
   * `ensureProject` (the sidebar, always mounted since F-0363) and `CustomViewsPage.tsx`'s own
   * mount-time `refreshViews` both call `refresh` for the same work area on one page load. Shared
   * only while pending — forgotten the moment it settles — so a later, deliberate `refresh` (Retry,
   * or the same `refreshViews` after a write this page made) still asks again, the same contract
   * `workAreaVersionScope.ts`'s own `readWorkAreaVersionScope` already holds (I-0679).
   */
  private readonly inFlightRefresh = createInFlightShare();
  /**
   * Derived lists, one per stored list: a reader that keys a memo or an effect on the list it was
   * handed (`ViewNavigationList`'s tree, its refocus effect) sees a new list only when the rows did
   * change.
   */
  private readonly customCache = new WeakMap<ISavedView[], ISavedView[]>();
  private readonly hierarchyCache = new WeakMap<ISavedView[], ISavedView[]>();

  constructor(
    private readonly dataSources: SavedViewDataSourceRegistry = savedViewDataSourceRegistry,
  ) {
    makeObservable(this, {
      // `observable.shallow`, not the plain (deep) default: every write here replaces a row
      // wholesale (`{...view, name: trimmed}`, never `view.name = trimmed`), so nothing needs a
      // saved view's own fields turned into MobX getter/setter pairs — and something downstream
      // needs them NOT to be. Found running this store's rows through `migrateSavedGenericViewDefinition`
      // for the first time (AC-19: `CustomViewsPage.tsx`, `DashboardPage.tsx` and
      // `WorkItemSavedViewSelector.tsx` all now read views from here, each already running that
      // same migration on whatever it reads): a deep-observable row's own nested
      // `IGenericViewDefinitionV2` — `filters` — silently failed migration and the row vanished
      // from every one of those three screens' catalogs, though `views(projectId)` itself still
      // listed it. A plain object with the same shape, read back out and run through the same
      // migration, migrates fine — this was never about the data, only about what MobX had turned
      // it into.
      viewsByProjectId: observable.shallow,
      loadingProjectIds: observable,
      heldErrorsByProjectId: observable,
      errorsByProjectId: computed,
      reorderingProjectIds: observable,
      heldOrderErrorsByProjectId: observable,
      orderErrorsByProjectId: computed,
      dismissOrderError: action,
      ensureProject: action,
      refresh: action,
      reorderViews: action,
      moveView: action,
      createFolder: action,
      renameView: action,
      deleteFolder: action,
      setBuiltInHidden: action,
      resetBuiltIn: action,
    });
  }

  /** Every stored row, a built-in's placement included (I-1335). */
  private rows(projectId: string): ISavedView[] {
    const rows = this.viewsByProjectId[projectId];
    return Array.isArray(rows) ? rows : [];
  }

  /**
   * Never anything but an array, whatever `refresh` was handed — `.filter`/`.map` everywhere else
   * in this store and in `ViewNavigationList` assume it (I-0697: a malformed REST body crashed the
   * sidebar's own `customRows.filter` straight to the error screen before `refresh` validated it;
   * this is the second guard, not the only one, in case something else ever sets this map directly).
   */
  views(projectId?: string | null): ISavedView[] {
    if (!projectId) return [];
    const rows = this.rows(projectId);
    // I-1335: a built-in's placement is not a view of its own; it is in `hierarchy`, not here.
    let custom = this.customCache.get(rows);
    if (!custom) {
      custom = rows.filter((row) => row.kind !== 'builtin');
      this.customCache.set(rows, custom);
    }
    return custom;
  }

  /**
   * The view tree's rows (I-1335): the custom views and folders, and every built-in view that is not
   * hidden, each built-in under its catalog id (`viewHierarchyRows.ts`).
   */
  hierarchy(projectId?: string | null): ISavedView[] {
    if (!projectId) return [];
    const rows = this.rows(projectId);
    let tree = this.hierarchyCache.get(rows);
    if (!tree) {
      tree = hierarchyRows(projectId, rows);
      this.hierarchyCache.set(rows, tree);
    }
    return tree;
  }

  /** Whether a built-in view was moved, so it has a placement row a Reset would drop (I-1335). */
  isBuiltInPlaced(projectId: string, seedId: string): boolean {
    return this.rows(projectId).some((row) => row.kind === 'builtin' && row.seedId === seedId);
  }

  /** Every built-in view, hidden or not, placed or not, under its catalog id (I-1335). */
  builtIns(projectId?: string | null): ISavedView[] {
    return projectId ? builtInRows(projectId, this.rows(projectId)) : [];
  }

  isLoading(projectId?: string | null): boolean {
    return Boolean(projectId && this.loadingProjectIds.includes(projectId));
  }

  isReordering(projectId?: string | null): boolean {
    return Boolean(projectId && this.reorderingProjectIds.includes(projectId));
  }

  orderError(projectId?: string | null): string | undefined {
    return projectId ? this.orderErrorsByProjectId[projectId] : undefined;
  }

  /** The initial-load failure, if the last attempt to read this work area's rows did not. */
  loadError(projectId?: string | null): string | undefined {
    return projectId ? this.errorsByProjectId[projectId] : undefined;
  }

  /**
   * Whether at least one load for this work area has finished — successfully or not — since this
   * store last forgot it. `ensureProject` is shared across every caller now (I-1064, AC-19): the
   * caller that loses the synchronous race to start the real `refresh` still has its own
   * `ensureProject` promise settle at once, before the real fetch a sibling caller already started
   * does — so a reader deciding whether to show a default before a routed view has had its real
   * chance to appear (`CustomViewsPage.tsx`'s own `awaitingRoutedView`) reads this, not that
   * promise.
   *
   * T-0559: the observable part is read first, every time. `requestSequences` is a plain map, so a
   * reader that asked before any load began, and stopped at it, followed nothing a load changes:
   * the phone's bottom bar (`useToolbarPlaceState`) kept a deleted view's slot, because only the
   * sidebar it no longer draws on a phone had ever made it look again.
   */
  hasLoaded(projectId?: string | null): boolean {
    const loading = this.isLoading(projectId);
    return Boolean(projectId && !loading && this.requestSequences.has(projectId));
  }

  dismissOrderError(projectId: string): void {
    this.heldOrderErrorsByProjectId = {
      ...this.heldOrderErrorsByProjectId,
      [projectId]: undefined,
    };
  }

  /**
   * Loads this work area's rows once — successfully or not. A reader who switches away and back
   * does not re-fetch; neither does a caller that asks after another already has, however much
   * later: `CustomViewsPage.tsx` is behind `lazyRoute`, so its own mount effect can run in a
   * *later* React commit than the sidebar's own immediate one, well after a fast-failing first
   * attempt has already settled (found live, `sidebar-views.spec.ts`'s own "Retry still asks
   * again, on top of the one arrival GET": 2 GETs on arrival instead of 1, on the failing branch
   * only). `requestSequences` is set as the very first, synchronous line of `refresh`, before
   * anything async — checking it, not `loadingProjectIds`/`viewsByProjectId` alone, is what keeps
   * a late caller from reading "not loading, nothing cached" as "never asked" and firing a second
   * real request. Only `refresh` itself tries again after a failure — Retry, or a write's own
   * nudge.
   */
  async ensureProject(projectId: string): Promise<void> {
    if (this.requestSequences.has(projectId)) return;
    await this.refresh(projectId);
  }

  async refresh(projectId: string): Promise<void> {
    const sequence = (this.requestSequences.get(projectId) ?? 0) + 1;
    this.requestSequences.set(projectId, sequence);
    runInAction(() => {
      if (!this.loadingProjectIds.includes(projectId)) {
        this.loadingProjectIds = [...this.loadingProjectIds, projectId];
      }
      this.heldErrorsByProjectId = { ...this.heldErrorsByProjectId, [projectId]: undefined };
    });
    try {
      const views = await this.inFlightRefresh(projectId, () =>
        this.dataSources.forProject(projectId).findAll(projectId),
      );
      if (this.requestSequences.get(projectId) !== sequence) return;
      // A 200 that is not a list is not "no views" — treated as a load failure below (I-0697),
      // not coerced to `[]` here: that would read as a genuinely empty work area when it is really
      // an answer this cannot trust.
      if (!Array.isArray(views)) {
        throw new Error('The server’s answer for this work area’s views was not a list.');
      }
      runInAction(() => {
        this.viewsByProjectId = { ...this.viewsByProjectId, [projectId]: views };
        this.loadingProjectIds = this.loadingProjectIds.filter((id) => id !== projectId);
      });
    } catch (cause) {
      if (this.requestSequences.get(projectId) !== sequence) return;
      runInAction(() => {
        this.heldErrorsByProjectId = {
          ...this.heldErrorsByProjectId,
          [projectId]: failureToSay(cause, 'views.actions.loadViews'),
        };
        this.loadingProjectIds = this.loadingProjectIds.filter((id) => id !== projectId);
      });
    }
  }

  /**
   * Puts one folder's children in `viewIds` order, first to last — the top level's when
   * `parentFolderId` is null (F-0363 slice 2, AC-10/AC-12).
   *
   * The new order shows at once and the save follows, for the reason `DashboardPageStore.
   * reorderPages` does: a tree that only redraws when the server answers reads as a drag that did
   * not take. A refused save puts back exactly the order that was on screen and says why.
   */
  async reorderViews(
    projectId: string,
    viewIds: string[],
    parentFolderId: string | null = null,
  ): Promise<boolean> {
    const previous = this.rows(projectId);
    // I-1335: a built-in never moved has no row and stands first, in coded order. Those the new
    // order still has there are left out of it, so a reorder below them places none of them; any
    // other is named, and gets its row here, as the server makes it.
    const placed = new Set(previous.map(treeIdOf));
    const unplaced = this.builtIns(projectId)
      .map((row) => row.id)
      .filter((id) => !placed.has(id));
    let first = 0;
    while (parentFolderId === null && viewIds[first] === unplaced[first]) first += 1;
    viewIds = viewIds.slice(first);
    const withPlacements = [
      ...previous,
      ...this.builtIns(projectId).filter((row) => viewIds.includes(row.id) && !placed.has(row.id)),
    ];
    const byId = new Map(withPlacements.map((view) => [treeIdOf(view), view]));
    const ordered = new Set(viewIds);
    // Only the level being ordered changes. Its rows are written back into the slots they already
    // held, in the new order, so every other row — and every other folder — keeps its place.
    let slot = 0;
    const reordered = withPlacements.map((view) => {
      if (!ordered.has(treeIdOf(view))) return view;
      const next = byId.get(viewIds[slot]);
      slot += 1;
      return next ? { ...next, position: slot * 10 } : view;
    });
    this.requestSequences.set(projectId, (this.requestSequences.get(projectId) ?? 0) + 1);
    runInAction(() => {
      this.viewsByProjectId = { ...this.viewsByProjectId, [projectId]: reordered };
      this.reorderingProjectIds = [...this.reorderingProjectIds, projectId];
      this.heldOrderErrorsByProjectId = {
        ...this.heldOrderErrorsByProjectId,
        [projectId]: undefined,
      };
    });
    try {
      await this.dataSources.forProject(projectId).reorder(projectId, viewIds, parentFolderId);
      return true;
    } catch (cause) {
      const conflict = httpStatus(cause) === 409;
      runInAction(() => {
        this.viewsByProjectId = { ...this.viewsByProjectId, [projectId]: previous };
        this.heldOrderErrorsByProjectId = {
          ...this.heldOrderErrorsByProjectId,
          [projectId]: conflict
            ? pageWords()('navigation.reorder.store.viewsChanged')
            : httpStatus(cause) === 403
              ? pageWords()('navigation.reorder.store.cannotReorderViews')
              : (serverMessage(cause) ?? failureToSay(cause, 'views.actions.reorder')),
        };
      });
      return false;
    } finally {
      runInAction(() => {
        this.reorderingProjectIds = this.reorderingProjectIds.filter((id) => id !== projectId);
      });
      // Either way the server's answer is the one to show next: the saved order, or — after a
      // conflict — the rows someone else created, moved or deleted.
      void this.refresh(projectId);
    }
  }

  /**
   * Moves a view or folder under another folder, or to the top level with `null` (F-0363 slice 2,
   * AC-9/AC-10/AC-12). A refused move puts back exactly the tree that was on screen and says why —
   * the server refuses a cycle, a non-folder parent and a seed id each naming the rows involved, so
   * its own sentence is the one worth showing.
   */
  async moveView(
    projectId: string,
    viewId: string,
    parentFolderId: string | null,
    position?: number,
  ): Promise<boolean> {
    const previous = this.rows(projectId);
    const seedId = builtInSeedIdOf(viewId);
    // I-1335: a built-in is moved by its seed; never moved before, its placement is made here.
    const known = previous.some((view) => treeIdOf(view) === viewId);
    const rows = known
      ? previous
      : [...previous, ...this.builtIns(projectId).filter((row) => row.id === viewId)];
    const moved = rows.map((view) =>
      treeIdOf(view) === viewId ? { ...view, parentFolderId } : view,
    );
    this.requestSequences.set(projectId, (this.requestSequences.get(projectId) ?? 0) + 1);
    runInAction(() => {
      this.viewsByProjectId = { ...this.viewsByProjectId, [projectId]: moved };
      this.reorderingProjectIds = [...this.reorderingProjectIds, projectId];
      this.heldOrderErrorsByProjectId = {
        ...this.heldOrderErrorsByProjectId,
        [projectId]: undefined,
      };
    });
    try {
      const source = this.dataSources.forProject(projectId);
      if (seedId) await source.placeBuiltIn(projectId, seedId, { parentFolderId, position });
      else await source.move(projectId, viewId, { parentFolderId, position });
      return true;
    } catch (cause) {
      runInAction(() => {
        this.viewsByProjectId = { ...this.viewsByProjectId, [projectId]: previous };
        this.heldOrderErrorsByProjectId = {
          ...this.heldOrderErrorsByProjectId,
          [projectId]:
            httpStatus(cause) === 403
              ? pageWords()('navigation.reorder.store.cannotMoveViews')
              : (serverMessage(cause) ?? failureToSay(cause, 'views.actions.moveView')),
        };
      });
      return false;
    } finally {
      runInAction(() => {
        this.reorderingProjectIds = this.reorderingProjectIds.filter((id) => id !== projectId);
      });
      void this.refresh(projectId);
    }
  }

  /**
   * Creates an empty folder for grouping custom views (F-0363 slice 2); never a seed, never a bare
   * view. Appended once the server confirms it — unlike a reorder or a move, there is no prior
   * screen state to show optimistically, so this does not guess the row's final id or position.
   */
  /**
   * Renames a row — a folder or a plain view, F2 in `ViewNavigationList.tsx` (F-0363 slice 2,
   * AC-13). Shows the new name at once and rolls back to the old one on a refusal, the same
   * optimistic contract `reorderViews`/`moveView` hold — a rename is not exempt from the rule that
   * a tree redrawing only once the server answers reads as an edit that did not take.
   */
  async renameView(projectId: string, viewId: string, name: string): Promise<boolean> {
    // I-1335: a built-in's name is the product's, read by every member; refused before it is sent,
    // with the server's own reason.
    if (builtInSeedIdOf(viewId)) {
      const builtIn = this.builtIns(projectId).find((row) => row.id === viewId);
      runInAction(() => {
        this.heldOrderErrorsByProjectId = {
          ...this.heldOrderErrorsByProjectId,
          [projectId]: builtInRenameRefusal(builtIn?.name ?? 'This view'),
        };
      });
      return false;
    }
    const previous = this.rows(projectId);
    const trimmed = name.trim();
    const renamed = previous.map((view) =>
      view.id === viewId ? { ...view, name: trimmed } : view,
    );
    runInAction(() => {
      this.viewsByProjectId = { ...this.viewsByProjectId, [projectId]: renamed };
      this.heldOrderErrorsByProjectId = {
        ...this.heldOrderErrorsByProjectId,
        [projectId]: undefined,
      };
    });
    try {
      await this.dataSources.forProject(projectId).update(projectId, viewId, { name: trimmed });
      return true;
    } catch (cause) {
      runInAction(() => {
        this.viewsByProjectId = { ...this.viewsByProjectId, [projectId]: previous };
        this.heldOrderErrorsByProjectId = {
          ...this.heldOrderErrorsByProjectId,
          [projectId]:
            httpStatus(cause) === 403
              ? pageWords()('navigation.reorder.store.cannotRenameRow')
              : (serverMessage(cause) ?? failureToSay(cause, 'views.actions.rename')),
        };
      });
      return false;
    } finally {
      void this.refresh(projectId);
    }
  }

  /**
   * Deletes an empty folder (F-0363 slice 2, AC-14) — never a plain view, which
   * `CustomViewsPage.tsx`'s own delete flow already owns. The row disappears at once; a refusal
   * (a folder that gained a child in another session since this tree last loaded, or one that was
   * never empty) restores it and shows the server's own sentence — `"X" has N items in it…` — the
   * same order-error banner a refused reorder already uses, rather than a second error channel.
   *
   * `moveContentsOut` (I-1390, the folder menu's "Delete folder"): a folder that holds things is
   * deleted too, and what it held takes its place one level up, in its own order — the same result
   * the server computes, so the refresh that follows changes nothing on screen.
   */
  async deleteFolder(
    projectId: string,
    folderId: string,
    moveContentsOut = false,
  ): Promise<boolean> {
    const previous = this.rows(projectId);
    const withoutFolder = moveContentsOut
      ? withoutFolderMovingContentsOut(previous, folderId)
      : previous.filter((view) => view.id !== folderId);
    runInAction(() => {
      this.viewsByProjectId = { ...this.viewsByProjectId, [projectId]: withoutFolder };
      this.heldOrderErrorsByProjectId = {
        ...this.heldOrderErrorsByProjectId,
        [projectId]: undefined,
      };
    });
    try {
      const source = this.dataSources.forProject(projectId);
      await (moveContentsOut
        ? source.remove(projectId, folderId, true)
        : source.remove(projectId, folderId));
      return true;
    } catch (cause) {
      runInAction(() => {
        this.viewsByProjectId = { ...this.viewsByProjectId, [projectId]: previous };
        this.heldOrderErrorsByProjectId = {
          ...this.heldOrderErrorsByProjectId,
          [projectId]:
            httpStatus(cause) === 403
              ? pageWords()('navigation.reorder.store.cannotDeleteFolder')
              : (serverMessage(cause) ?? failureToSay(cause, 'views.actions.deleteFolder')),
        };
      });
      return false;
    } finally {
      void this.refresh(projectId);
    }
  }

  /**
   * I-1335: hides a built-in view from the work area's tree, or shows it again — shared by every
   * member, so it is a `view.manage` write like every other here. Hidden, it keeps its place.
   */
  async setBuiltInHidden(projectId: string, seedId: string, hidden: boolean): Promise<boolean> {
    const treeId = `default-view:${seedId}`;
    const previous = this.rows(projectId);
    const known = previous.some((view) => treeIdOf(view) === treeId);
    const rows = known
      ? previous
      : [...previous, ...this.builtIns(projectId).filter((row) => row.id === treeId)];
    return this.writeBuiltIn(
      projectId,
      previous,
      rows.map((view) => (treeIdOf(view) === treeId ? { ...view, hidden } : view)),
      (source) => source.placeBuiltIn(projectId, seedId, { hidden }),
      hidden ? 'views.actions.hide' : 'views.actions.show',
    );
  }

  /** I-1335's Reset position: the placement goes; the built-in is first again, and shown. */
  async resetBuiltIn(projectId: string, seedId: string): Promise<boolean> {
    const treeId = `default-view:${seedId}`;
    const previous = this.rows(projectId);
    return this.writeBuiltIn(
      projectId,
      previous,
      previous.filter((view) => treeIdOf(view) !== treeId),
      (source) => source.resetBuiltIn(projectId, seedId),
      'views.actions.resetPosition',
    );
  }

  private async writeBuiltIn(
    projectId: string,
    previous: ISavedView[],
    next: ISavedView[],
    write: (source: ReturnType<SavedViewDataSourceRegistry['forProject']>) => Promise<unknown>,
    what: MessageKey,
  ): Promise<boolean> {
    this.requestSequences.set(projectId, (this.requestSequences.get(projectId) ?? 0) + 1);
    runInAction(() => {
      this.viewsByProjectId = { ...this.viewsByProjectId, [projectId]: next };
      this.heldOrderErrorsByProjectId = {
        ...this.heldOrderErrorsByProjectId,
        [projectId]: undefined,
      };
    });
    try {
      await write(this.dataSources.forProject(projectId));
      return true;
    } catch (cause) {
      runInAction(() => {
        this.viewsByProjectId = { ...this.viewsByProjectId, [projectId]: previous };
        this.heldOrderErrorsByProjectId = {
          ...this.heldOrderErrorsByProjectId,
          [projectId]:
            httpStatus(cause) === 403
              ? pageWords()('navigation.reorder.store.cannotArrangeViews')
              : (serverMessage(cause) ?? failureToSay(cause, what)),
        };
      });
      return false;
    } finally {
      void this.refresh(projectId);
    }
  }

  async createFolder(
    projectId: string,
    name: string,
    parentFolderId: string | null = null,
  ): Promise<ISavedView | null> {
    try {
      const folder = await this.dataSources
        .forProject(projectId)
        .createFolder(projectId, { name, parentFolderId });
      runInAction(() => {
        this.viewsByProjectId = {
          ...this.viewsByProjectId,
          [projectId]: [...this.rows(projectId), folder],
        };
      });
      return folder;
    } catch (cause) {
      runInAction(() => {
        this.heldOrderErrorsByProjectId = {
          ...this.heldOrderErrorsByProjectId,
          [projectId]:
            httpStatus(cause) === 403
              ? pageWords()('navigation.reorder.store.cannotCreateFolders')
              : (serverMessage(cause) ?? failureToSay(cause, 'views.actions.createFolder')),
        };
      });
      return null;
    }
  }
}

/** The server's own sentence for a refused rename of a built-in view (I-1335). */
export function builtInRenameRefusal(name: string): string {
  return `“${name}” is a built-in view: its name is the product's and every member reads it. Save a copy to give it a name of its own.`;
}

function httpStatus(cause: unknown): number | undefined {
  return (cause as { response?: { status?: number } })?.response?.status;
}

/**
 * What the server said, where it said something worth reading. A refused move or reorder names
 * the rows and why — "…is already inside…", "…is a view, not a folder…" — and no sentence written
 * here could be as useful as that one.
 */
function serverMessage(cause: unknown): string | undefined {
  const message = (cause as { response?: { data?: { message?: unknown } } })?.response?.data
    ?.message;
  if (typeof message === 'string' && message.trim()) return message.trim();
  if (Array.isArray(message)) {
    const first = message.find((entry) => typeof entry === 'string' && entry.trim());
    if (typeof first === 'string') return first.trim();
  }
  return undefined;
}

/**
 * `rows` without the folder, what it held standing in its place at the folder's own level, in the
 * order they had (I-1390). Positions are renumbered the way the server renumbers them.
 */
export function withoutFolderMovingContentsOut(rows: ISavedView[], folderId: string): ISavedView[] {
  const folder = rows.find((row) => row.id === folderId);
  if (!folder) return rows;
  const parentFolderId = folder.parentFolderId ?? null;
  const byPosition = (a: ISavedView, b: ISavedView) => (a.position ?? 0) - (b.position ?? 0);
  const contents = rows.filter((row) => row.parentFolderId === folderId).sort(byPosition);
  const level = rows.filter((row) => (row.parentFolderId ?? null) === parentFolderId);
  const placed = new Map<string, ISavedView>();
  level
    .sort(byPosition)
    .flatMap((row) => (row.id === folderId ? contents : [row]))
    .forEach((row, index) =>
      placed.set(row.id, { ...row, parentFolderId, position: (index + 1) * 10 }),
    );
  return rows.filter((row) => row.id !== folderId).map((row) => placed.get(row.id) ?? row);
}
